import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { $crud } from "../helper/CrudFactory";
import LanguageContext from "../helper/LanguageContext";
import appConfig from "../config/appConfig";
import { SettingsBanner, SettingsNote, SettingsRow, SettingsSection, SettingsTag } from "./settings/SettingsList";
import {
  listTeamsAccounts,
  getTeamsAuthUrl,
  getTeamsAdminConsentUrl,
  syncTeamsAccount,
  disconnectTeamsAccount,
} from "../helper/teamsApi";
import {
  TEAMS_ACCOUNT_EVENT,
  TEAMS_CONNECT_RESULT_EVENT,
  handleTeamsReturn,
  mergeTeamsAccount,
  parseTeamsReturn,
  teamsDisconnectErrorMessage,
  teamsErrorMessage,
  teamsReturnFromMessage,
} from "../utils/teams/teamsConnect";
import { TEAMS_NEW_ACCOUNT, buildTeamsAccountsView, teamsAccountLabel, teamsSettingsRowState } from "../utils/teams/teamsSettingsModel";
import "./TodoistSyncSettings.css";
import "./TeamsSyncSettings.css";

// Polling fallback for the socket progress while something is running on the server.
const POLL_MS = 15000;
const POLL_STATES = ["connecting", "importing", "removing"];

const apiOrigin = (() => {
  try {
    return new URL(appConfig.API_URL).origin;
  } catch (_) {
    return null;
  }
})();

/**
 * Settings → Microsoft Teams (Kjuup.io), mobile TeamsSettingsScreen + TeamsSettingsPro. Owner of the
 * Teams connections on the web: one card per connected Microsoft account plus "Add another
 * account". Connect (popup → backend result page → postMessage or the return URL →
 * POST /teams/accounts/complete), import progress (socket 'teamsAccount', relayed by App.js, with a
 * polling fallback), Sync now, admin consent and Disconnect, each for its own account.
 *
 * `locked`: Kjuup.io lapsed. Connect and Sync are locked then; an existing account or archive can
 * still be disconnected and deleted. `onRowState` gets the Settings row state after every load.
 */
export default function TeamsSyncSettings({ strings = {}, locked = false, onRowState }) {
  const { isGerm } = React.useContext(LanguageContext);
  const t = (key, fallback) => strings[key] || fallback;
  const subscription = useSelector((s) => s.subscription) || {};
  const isKjuupPlusMember = !!subscription.isKjuupPlusMember;
  const history = useHistory();
  const location = useLocation();
  const [enabled, setEnabled] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  // Busy states hold the id of the account they run for (TEAMS_NEW_ACCOUNT: connect of another one).
  const [connecting, setConnecting] = useState(null);
  const [syncing, setSyncing] = useState(null);
  const [disconnecting, setDisconnecting] = useState(null);
  const [consentBusy, setConsentBusy] = useState(null);
  const [error, setError] = useState(null);
  const [dialog, setDialog] = useState(null); // { kind: 'disconnect' | 'purge', account }
  const mountedRef = useRef(true);
  const popupRef = useRef(null);
  const popupTimerRef = useRef(null);
  const stringsRef = useRef(strings);
  stringsRef.current = strings;
  const onRowStateRef = useRef(onRowState);
  onRowStateRef.current = onRowState;

  const fetchAccounts = useCallback(async () => {
    const r = await listTeamsAccounts();
    if (!mountedRef.current) return null;
    if (r.ok) {
      setEnabled(r.enabled);
      setAccounts(r.accounts);
    }
    const row = teamsSettingsRowState(r);
    if (row && onRowStateRef.current) onRowStateRef.current(row);
    setLoading(false);
    return r;
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    fetchAccounts();
    return () => {
      mountedRef.current = false;
      if (popupTimerRef.current) clearInterval(popupTimerRef.current);
    };
  }, [fetchAccounts]);

  // The sign-in came back through the return URL (no opener: popup blocked by the browser or the
  // result page opened on its own): redeem it once, then drop the code from the address bar.
  useEffect(() => {
    const link = parseTeamsReturn(location.search);
    if (!link) return;
    handleTeamsReturn(link, { strings: stringsRef.current }).catch(() => {});
    history.replace({ pathname: location.pathname, search: "?section=teams", state: location.state });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // The backend's result page in the popup posts the outcome (with the one-time code) to this origin.
  useEffect(() => {
    const onMessage = (event) => {
      const link = teamsReturnFromMessage(event && event.data);
      if (!link) return;
      const fromPopup = popupRef.current && event.source === popupRef.current;
      if (!fromPopup && apiOrigin && event.origin !== apiOrigin) return;
      handleTeamsReturn(link, { strings: stringsRef.current }).catch(() => {});
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  // Outcome of a connect (from the message above or the return URL).
  useEffect(() => {
    const onResult = (e) => {
      const result = e && e.detail;
      if (!result || !mountedRef.current) return;
      setConnecting(null);
      if (result.ok) {
        setError(null);
        $crud.notify({ type: "success", message: result.message });
      } else {
        setError({ code: result.code, message: result.message });
      }
      fetchAccounts();
    };
    window.addEventListener(TEAMS_CONNECT_RESULT_EVENT, onResult);
    return () => window.removeEventListener(TEAMS_CONNECT_RESULT_EVENT, onResult);
  }, [fetchAccounts]);

  // Socket 'teamsAccount' (relayed by App.js): progress and status of one of the accounts.
  useEffect(() => {
    const onAccount = (e) => {
      const patch = e && e.detail && e.detail.account;
      if (!patch) return;
      setAccounts((prev) => prev.map((a) => mergeTeamsAccount(a, patch)));
    };
    window.addEventListener(TEAMS_ACCOUNT_EVENT, onAccount);
    return () => window.removeEventListener(TEAMS_ACCOUNT_EVENT, onAccount);
  }, []);

  const watchPopup = (popup) => {
    if (popupTimerRef.current) clearInterval(popupTimerRef.current);
    popupTimerRef.current = setInterval(() => {
      if (!popup || popup.closed) {
        clearInterval(popupTimerRef.current);
        popupTimerRef.current = null;
        if (popupRef.current === popup) popupRef.current = null;
        if (mountedRef.current) setConnecting(null);
        fetchAccounts();
      }
    }, 500);
  };

  // account: reconnect that one; null: add another Microsoft account.
  const openConnect = async (account) => {
    if (locked || connecting != null) return;
    setConnecting(account ? String(account.id) : TEAMS_NEW_ACCOUNT);
    setError(null);
    // Opened at once, inside the click, so the browser does not block it; the sign-in page is
    // loaded into it once the backend returned its URL.
    let popup = null;
    try {
      popup = window.open("", "kjuup-teams-oauth", "width=600,height=720,scrollbars=yes");
    } catch (_) {
      popup = null;
    }
    const r = await getTeamsAuthUrl({ accountId: account ? account.id : null });
    if (!mountedRef.current) return;
    if (!r.ok) {
      if (popup && !popup.closed) popup.close();
      setConnecting(null);
      setError({ code: r.code, message: teamsErrorMessage(r.code, r.message, strings) });
      return;
    }
    if (!popup || popup.closed) {
      // Popups blocked: the sign-in runs in this tab and comes back to this page with the code.
      window.location.assign(r.url);
      return;
    }
    try {
      popup.location.href = r.url;
      popup.focus();
    } catch (_) {
      window.location.assign(r.url);
      return;
    }
    popupRef.current = popup;
    watchPopup(popup);
  };

  const onSync = async (account) => {
    if (!account || syncing != null) return;
    setSyncing(String(account.id));
    const r = await syncTeamsAccount(account.id);
    if (!mountedRef.current) return;
    setSyncing(null);
    if (r.ok) {
      $crud.notify({
        type: "success",
        message: r.code === "already_queued" ? t("teams_sync_already", "A sync is already running.") : t("teams_sync_queued", "Sync started"),
      });
      fetchAccounts();
    } else {
      $crud.notify({ type: "error", message: teamsErrorMessage(r.code, r.message, strings) });
    }
  };

  const runDisconnect = async (account, keepReadOnly) => {
    if (!account) return;
    setDialog(null);
    setDisconnecting(String(account.id));
    const r = await disconnectTeamsAccount(account.id, { keepReadOnly });
    if (!mountedRef.current) return;
    setDisconnecting(null);
    if (r.ok) {
      setError(null);
      if (r.account) setAccounts((prev) => prev.map((a) => (String(a.id) === String(r.account.id) ? { ...a, ...r.account } : a)));
      $crud.notify({ type: "success", message: r.message || t("teams_disconnected", "Microsoft Teams disconnected") });
      fetchAccounts();
    } else {
      $crud.notify({ type: "error", message: teamsDisconnectErrorMessage(strings) });
    }
  };

  const adminConsent = async (account, open) => {
    if (consentBusy != null) return;
    // The approval page opens in a tab reserved inside the click (popup blockers).
    let tab = null;
    if (open) {
      try {
        tab = window.open("", "_blank");
      } catch (_) {
        tab = null;
      }
    }
    setConsentBusy(account ? String(account.id) : TEAMS_NEW_ACCOUNT);
    const r = await getTeamsAdminConsentUrl({ accountId: account ? account.id : null });
    if (!mountedRef.current) return;
    setConsentBusy(null);
    if (!r.ok) {
      if (tab && !tab.closed) tab.close();
      $crud.notify({
        type: "error",
        message: r.code ? teamsErrorMessage(r.code, r.message, strings) : t("teams_admin_link_failed", "The approval link could not be created."),
      });
      return;
    }
    if (open) {
      if (tab && !tab.closed) {
        tab.opener = null;
        tab.location.href = r.url;
      } else {
        window.open(r.url, "_blank", "noopener,noreferrer");
      }
      return;
    }
    const text = t("teams_share_message", "Please approve Kjuup for Microsoft Teams in our organization: {{url}}").replace(/\{\{\s*url\s*\}\}/g, r.url);
    try {
      await navigator.clipboard.writeText(text);
      $crud.notify({ type: "success", message: t("teams_consent_copied", "Approval link copied. Send it to your IT admin.") });
    } catch (_) {
      // No clipboard access: show the link so it can be copied by hand.
      window.prompt(t("teams_consent_copy", "Copy link for IT admin"), r.url);
    }
  };

  const lockedText = locked
    ? isKjuupPlusMember
      ? t("kjuup_io_company_managed_body_short", "Ask your company admin to assign you a Kjuup.io seat in the Admin Panel.")
      : t("kjuup_io_trial_ended_body", "Subscribe to Kjuup.io to keep using calendar and other premium features.")
    : null;

  const view = buildTeamsAccountsView({
    accounts,
    enabled,
    locked: lockedText,
    error,
    strings,
    busy: { connecting, syncing, disconnecting, consent: consentBusy },
    handlers: {
      connect: openConnect,
      sync: onSync,
      disconnect: (account) => setDialog({ kind: "disconnect", account }),
      purge: (account) => setDialog({ kind: "purge", account }),
      copyConsent: (account) => adminConsent(account, false),
      openConsent: (account) => adminConsent(account, true),
    },
  });

  const polling = view.cards.some((c) => POLL_STATES.includes(c.state));
  useEffect(() => {
    if (!polling) return undefined;
    const timer = setInterval(() => {
      fetchAccounts();
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [polling, fetchAccounts]);

  const cancelLabel = strings.cancel || (isGerm ? "Abbrechen" : "Cancel");
  const deleteLabel = t("teams_disconnect_delete", "Delete imported chats");
  const dialogKind = dialog && dialog.kind;
  const dialogAccount = dialog && dialog.account;
  const several = view.cards.length > 1;

  const actionButton = (a) => (
    <button
      key={a.key}
      type="button"
      className={`kj-btn ${a.kind === "secondary" ? "kj-btn--secondary" : "kj-btn--primary"} kj-btn--lg kj-btn--block${a.busy ? " kj-btn--loading" : ""}`}
      disabled={!!a.busy || !!a.disabled}
      onClick={a.onPress}
    >
      {a.icon ? <span className="material-symbols-outlined" aria-hidden="true">{a.icon}</span> : null}
      <span>{a.label}</span>
    </button>
  );

  const bannerOf = (b) => (
    <SettingsBanner
      tone={b.tone}
      icon={b.icon || (b.tone === "err" ? "sync_problem" : b.tone === "warn" ? "warning" : "info")}
      title={b.title}
      body={b.body}
    />
  );

  // One Microsoft account: its state banner, import progress, buttons and the account rows.
  const renderCard = (card) => (
    <div key={card.id} className="teams-sync-settings__account">
      {card.banner ? bannerOf(card.banner) : null}
      {card.progress ? (
        <div className="kj-settings-card teams-sync-settings__progress" role="status">
          <span className="teams-sync-settings__progress-label">{card.progress.label}</span>
          <span
            className="teams-sync-settings__bar"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round((card.progress.value || 0) * 100)}
          >
            <span className="teams-sync-settings__bar-fill" style={{ width: `${Math.round((card.progress.value || 0) * 100)}%` }} />
          </span>
          {card.progress.sub ? <span className="teams-sync-settings__progress-sub">{card.progress.sub}</span> : null}
        </div>
      ) : null}
      {card.note ? <SettingsNote className="teams-sync-settings__note">{card.note}</SettingsNote> : null}
      {card.primary.length ? <div className="teams-sync-settings__actions">{card.primary.map(actionButton)}</div> : null}
      {card.account || card.rows.length ? (
        <SettingsSection caption={card.account ? card.account.caption : undefined}>
          {card.account ? (
            <SettingsRow
              icon="groups"
              label={card.account.title}
              sub={card.account.sub || undefined}
              trailing={card.account.chip ? <SettingsTag tone={card.account.chip.tone}>{card.account.chip.label}</SettingsTag> : null}
            />
          ) : null}
          {card.rows.map((r) => (
            <SettingsRow
              key={r.key}
              icon={r.icon}
              label={r.label}
              danger={r.danger}
              busy={!!r.busy}
              chevron={false}
              onClick={r.busy ? undefined : r.onPress}
            />
          ))}
        </SettingsSection>
      ) : null}
    </div>
  );

  return (
    <div className="todoist-sync-settings todo-pro teams-sync-settings">
      <SettingsNote className="todo-pro__intro">
        {t(
          "teams_info",
          "Read and answer your Microsoft Teams chats in Kjuup. They appear in Messages with a TEAMS tag."
        )}
      </SettingsNote>

      {lockedText ? <SettingsBanner tone="warn" icon="lock" title={lockedText} /> : null}

      {loading ? (
        <div className="kj-settings-card todo-pro__skeleton" aria-busy="true">
          {[0, 1].map((i) => (
            <div key={i} className="todo-pro__bone-row">
              <span className="todo-pro__bone todo-pro__bone--tile" />
              <span className={`todo-pro__bone todo-pro__bone--line${i ? " is-short" : ""}`} />
            </div>
          ))}
        </div>
      ) : (
        <>
          {view.banner ? bannerOf(view.banner) : null}

          {view.primary.length ? <div className="teams-sync-settings__actions">{view.primary.map(actionButton)}</div> : null}

          {view.cards.map(renderCard)}

          {view.add ? <div className="teams-sync-settings__actions">{actionButton(view.add)}</div> : null}
        </>
      )}

      <Modal
        show={!!dialog}
        onHide={() => setDialog(null)}
        centered
        dialogClassName="ps-dialog"
        contentClassName="ps-dialog__content"
      >
        <div className="ps-dialog__header">
          <div className="ps-dialog__titles">
            <h4 className="ps-dialog__title">
              {dialogKind === "purge" ? deleteLabel : t("teams_disconnect_title", "Disconnect Microsoft Teams?")}
            </h4>
            {several && dialogAccount ? <p className="ps-dialog__subtitle">{teamsAccountLabel(dialogAccount)}</p> : null}
          </div>
          <button
            type="button"
            className="kj-btn kj-btn--icon kj-btn--neutral ps-dialog__close"
            onClick={() => setDialog(null)}
            aria-label={strings.close || cancelLabel}
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>
        <div className="ps-dialog__body">
          <p className="teams-sync-settings__dialog-text">
            {dialogKind === "purge"
              ? t("teams_delete_archive_body", "Your imported Teams chats will be permanently removed from Kjuup. Your chats in Microsoft Teams stay unchanged.")
              : t("teams_disconnect_body", "Kjuup stops syncing with Teams. Your chats in Microsoft Teams stay unchanged. What should happen to the imported chats in Kjuup?")}
          </p>
        </div>
        <div className="ps-dialog__footer teams-sync-settings__dialog-footer">
          <button type="button" className="kj-btn kj-btn--secondary" onClick={() => setDialog(null)}>
            {cancelLabel}
          </button>
          {dialogKind === "disconnect" ? (
            <button type="button" className="kj-btn kj-btn--secondary" onClick={() => runDisconnect(dialogAccount, true)}>
              {t("teams_disconnect_keep", "Keep as read-only")}
            </button>
          ) : null}
          <button type="button" className="kj-btn kj-btn--danger" onClick={() => runDisconnect(dialogAccount, false)}>
            {deleteLabel}
          </button>
        </div>
      </Modal>
    </div>
  );
}
